import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  Search,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Phone,
  Mail,
  BookOpen,
  MapPin,
  Globe,
  ExternalLink,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Power,
  PowerOff,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import adminService from '../../services/admin.service';
import { API_ENDPOINTS } from '../../config/api';

interface Consultant {
  id: string; 
  name: string;
  email: string;
  phone?: string;
  phone_number?: string;
  isActive: boolean;
  isVerified?: boolean;
  totalTrainings?: number;  
  profile_type?: 'consultant' | 'training_center';
  createdAt: string;
  user?: { id: string; email?: string };
  user_id?: string;
  address?: string;
  city?: string;
  website?: string;
  about?: string;
  company_name?: string;
  facebook_url?: string;
  linkedin_url?: string;
  logo?: string;
  country_id?: number;
  country?: { id: number; name: string };
  wilaya_id?: number;
  wilaya?: { id: number; name: string };
  subscriptionStatus?: string;
}

const ConsultantsPage: React.FC = () => {
  const { t } = useTranslation();
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<Consultant | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [consultantDetails, setConsultantDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    loadConsultants();
  }, []);

  const loadConsultants = async () => {
    try {
      setLoading(true);
      // First, get the total count with a minimal request
      const countResponse = await adminService.getConsultants(1, 1);
      const total = countResponse.total;
      
      // Then, fetch all consultants using the total count as limit
      if (total > 0) {
        const response = await adminService.getConsultants(1, total);
        setConsultants(response.consultants || []);
      } else {
        setConsultants([]);
      }
    } catch (error) {
      setConsultants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleConsultantStatus = async (consultant: Consultant) => {
    const newStatus = !consultant.isActive;
    const action = newStatus ? t('admin.consultants.toggle.activate') : t('admin.consultants.toggle.deactivate');
    
    if (!window.confirm(t('admin.consultants.toggle.confirm', { action, name: consultant.name }))) {
      return;
    }

    try {
      await adminService.updateConsultantStatus(consultant.id, newStatus, consultant.isVerified);
      setConsultants(prev => prev.map(c => 
        c.id === consultant.id ? { ...c, isActive: newStatus } : c
      ));
      // Also update if it's in the details modal
      if (selectedConsultant?.id === consultant.id) {
        setSelectedConsultant({ ...selectedConsultant, isActive: newStatus });
      }
      if (consultantDetails?.id === consultant.id) {
        setConsultantDetails({ ...consultantDetails, isActive: newStatus });
      }
    } catch (error) {
      alert(t('admin.consultants.toggle.error', { action }));
    }
  };

  const handleDeleteConsultant = async (consultant: Consultant) => {
    let userId = (consultant.user?.id || consultant.user_id || '').toString();

    // Fallback: fetch details to resolve user_id when not present in the list item
    if (!userId) {
      try {
        const details = await adminService.getConsultantById(Number(consultant.id as any));
        userId = (details?.user?.id || details?.user_id || details?.userId || '').toString();
      } catch (e) {
      }
    }

    if (!userId) {
      alert(t('admin.consultants.delete.userError'));
      return;
    }

    if (window.confirm(t('admin.consultants.delete.confirm'))) {
      try {
        await adminService.deleteConsultant(userId);
        setConsultants(prev => prev.filter(c => (c.user?.id || c.user_id) !== userId && c.id !== consultant.id));
      } catch (e) {
        alert(t('admin.consultants.delete.error'));
      }
    }
  };

  const getSubscriptionStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSubscriptionStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'expired': return 'Expiré';
      case 'pending': return 'En attente';
      default: return status || 'N/A';
    }
  };

  const filtered = consultants.filter(c =>
    ((c.profile_type ?? 'consultant') === 'consultant') && (
      (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.phone || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Pagination calculations
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedConsultants = filtered.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleViewDetails = async (consultant: Consultant) => {
    setSelectedConsultant(consultant);
    setShowDetailsModal(true);
    setLoadingDetails(true);
    try {
      const id = (consultant.id || '').toString();
      const details = await adminService.getConsultantById(Number(id));
      setConsultantDetails(details);
    } catch (error) {
      setConsultantDetails(consultant);
    } finally {
      setLoadingDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consultants</h1>
          <p className="text-gray-600 mt-1">Gérez les consultants de la plateforme</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 md:p-6">
        <div className="flex gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Rechercher des consultants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.consultants.table.consultant')}</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.consultants.table.contact')}</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.consultants.table.status')}</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Abonnement</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.consultants.table.trainings')}</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.consultants.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedConsultants.map((c) => (
                <tr key={(c.user?.id || c.user_id || c.id) as string} className="hover:bg-gray-50">
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-brand-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-brand-primary" />
                      </div>
                      <div className="ml-3 lg:ml-4 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{c.name || '—'}</div>
                        <div className="text-xs text-gray-500">{new Date(c.createdAt).toLocaleDateString('fr-FR')}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm text-gray-900">
                      <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{c.email}</span>
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {c.isActive ? (
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${c.isActive ? 'text-green-600' : 'text-red-600'}`}>
                        {c.isActive ? t('admin.consultants.status.active') : t('admin.consultants.status.inactive')}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSubscriptionStatusColor(c.subscriptionStatus || '')}`}>
                      {getSubscriptionStatusLabel(c.subscriptionStatus || '')}
                    </span>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {c.totalTrainings ?? 0}
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button className="text-brand-primary hover:opacity-90 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center" title={t('admin.consultants.actions.view')} onClick={() => handleViewDetails(c)}>
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleToggleConsultantStatus(c)}
                        className={`${c.isActive ? "text-orange-600 hover:text-orange-900" : "text-green-600 hover:text-green-900"} touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center`}
                        title={c.isActive ? t('admin.consultants.actions.deactivate') : t('admin.consultants.actions.activate')}
                      >
                        {c.isActive ? <PowerOff size={18} /> : <Power size={18} />}
                      </button>
                      <button
                        onClick={() => handleDeleteConsultant(c)}
                        className="text-red-600 hover:text-red-900 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                        title={t('admin.consultants.actions.delete')}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('admin.consultants.empty.title')}</p>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {paginatedConsultants.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('admin.consultants.empty.title')}</p>
          </div>
        ) : (
          paginatedConsultants.map((c) => (
            <div key={(c.user?.id || c.user_id || c.id) as string} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="h-10 w-10 bg-brand-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-brand-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">{c.name || '—'}</div>
                    <div className="text-xs text-gray-500">{new Date(c.createdAt).toLocaleDateString('fr-FR')}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <button className="text-brand-primary hover:opacity-90 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center" title={t('admin.consultants.actions.view')} onClick={() => handleViewDetails(c)}>
                    <Eye size={18} />
                  </button>
                  <button
                    onClick={() => handleToggleConsultantStatus(c)}
                    className={`${c.isActive ? "text-orange-600 hover:text-orange-900" : "text-green-600 hover:text-green-900"} touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center`}
                    title={c.isActive ? t('admin.consultants.actions.deactivate') : t('admin.consultants.actions.activate')}
                  >
                    {c.isActive ? <PowerOff size={18} /> : <Power size={18} />}
                  </button>
                  <button
                    onClick={() => handleDeleteConsultant(c)}
                    className="text-red-600 hover:text-red-900 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title={t('admin.consultants.actions.delete')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              
              <div className="space-y-2 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                  <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate break-all">{c.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  {c.isActive ? (
                    <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                  )}
                  <span className={`text-xs sm:text-sm ${c.isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {c.isActive ? t('admin.consultants.status.active') : t('admin.consultants.status.inactive')}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSubscriptionStatusColor(c.subscriptionStatus || '')}`}>
                      {getSubscriptionStatusLabel(c.subscriptionStatus || '')}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-600">
                      {c.totalTrainings ?? 0} {t('admin.consultants.table.trainings')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {filtered.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
            <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
              {t('admin.consultants.pagination.showing')} <span className="font-semibold">{startIndex + 1}</span> {t('admin.consultants.pagination.to')}{' '}
              <span className="font-semibold">{Math.min(endIndex, filtered.length)}</span> {t('admin.consultants.pagination.of')}{' '}
              <span className="font-semibold">{filtered.length}</span> {t('admin.consultants.pagination.consultants')}
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2 flex-wrap justify-center">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1 text-xs sm:text-sm touch-manipulation min-h-[44px]"
              >
                <ChevronLeft size={14} className="sm:hidden" />
                <ChevronLeft size={16} className="hidden sm:block" />
                <span className="hidden sm:inline">{t('admin.consultants.pagination.previous')}</span>
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-2 sm:px-3 py-2 min-w-[36px] sm:min-w-[40px] rounded-lg transition-colors text-xs sm:text-sm touch-manipulation min-h-[44px] ${
                          currentPage === page
                            ? 'bg-brand-primary text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-1 sm:px-2">...</span>;
                  }
                  return null;
                })}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1 text-xs sm:text-sm touch-manipulation min-h-[44px]"
              >
                <span className="hidden sm:inline">{t('admin.consultants.pagination.next')}</span>
                <ChevronRight size={14} className="sm:hidden" />
                <ChevronRight size={16} className="hidden sm:block" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal - similar style to TrainingCentersPage */}
      {showDetailsModal && selectedConsultant && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4" onClick={() => { setShowDetailsModal(false); setSelectedConsultant(null); setConsultantDetails(null); setIsAboutExpanded(false); }}>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-brand-primary to-purple-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-xl font-bold text-white truncate">{t('admin.consultants.modal.title')}</h2>
                  <p className="text-white/80 text-xs sm:text-sm truncate">{t('admin.consultants.modal.subtitle')}</p>
                </div>
              </div>
              <button onClick={() => { setShowDetailsModal(false); setSelectedConsultant(null); setConsultantDetails(null); setIsAboutExpanded(false); }} className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0">
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                  <span className="ml-3 text-xs sm:text-sm text-gray-600">{t('admin.consultants.modal.loading')}</span>
                </div>
              ) : (
                <div className="space-y-4 sm:space-y-6">
                  {/* Header with Logo and Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    {/* Logo */}
                    <div className="flex items-center justify-center">
                      {consultantDetails?.logo ? (
                        <img
                          src={consultantDetails.logo.startsWith('http') ? consultantDetails.logo : `${API_ENDPOINTS.UPLOADS}/${consultantDetails.logo}`}
                          alt={`Logo ${consultantDetails?.name ?? 'Consultant'}`}
                          className="w-32 h-32 object-contain rounded-lg border-2 border-gray-200"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-32 h-32 rounded-lg border-2 border-gray-200 flex items-center justify-center bg-gray-50">
                          <ImageIcon className="h-16 w-16 text-gray-400" />
                        </div>
                      )}
                    </div>
                    {/* Basic Info */}
                    <div className="md:col-span-2 space-y-2 min-w-0">
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{consultantDetails?.name ?? selectedConsultant?.name ?? 'Consultant'}</h3>
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                        <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-brand-primary flex-shrink-0" />
                        <span className="truncate break-all">{consultantDetails?.email ?? consultantDetails?.user?.email ?? selectedConsultant?.email ?? '—'}</span>
                      </div>
                      {(consultantDetails?.phone || consultantDetails?.phone_number || selectedConsultant?.phone) ? (
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Phone className="h-4 w-4 text-brand-primary" />
                          <span>{consultantDetails?.phone ?? consultantDetails?.phone_number ?? selectedConsultant?.phone ?? 'Non renseigné'}</span>
                        </div>
                      ) : null}
                      {/* Location */}
                      {(consultantDetails?.address || consultantDetails?.city || consultantDetails?.wilaya || consultantDetails?.country) && (
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <MapPin className="h-4 w-4 text-gray-600" />
                          <span>
                            {consultantDetails?.address ? `${consultantDetails.address}, ` : ''}
                            {consultantDetails?.city ? `${consultantDetails.city}, ` : ''}
                            {consultantDetails?.wilaya?.name ? `${consultantDetails.wilaya.name}, ` : ''}
                            {consultantDetails?.country?.name ?? ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* About */}
                  {consultantDetails?.about && (() => {
                    const aboutText = consultantDetails.about;
                    const maxLength = 300;
                    const shouldTruncate = aboutText.length > maxLength;
                    const displayText = isAboutExpanded ? aboutText : (shouldTruncate ? aboutText.substring(0, maxLength) + '...' : aboutText);
                    
                    return (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('admin.consultants.modal.about')}</h3>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                          {displayText}
                        </p>
                        {shouldTruncate && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsAboutExpanded(prev => !prev);
                            }}
                            className="mt-2 text-brand-primary hover:text-brand-secondary font-medium text-sm transition-colors flex items-center gap-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/20 rounded px-1"
                          >
                            {isAboutExpanded ? (
                              <>
                                <span>{t('admin.consultants.modal.readLess')}</span>
                                <ChevronUp className="h-4 w-4" />
                              </>
                            ) : (
                              <>
                                <span>{t('admin.consultants.modal.readMore')}</span>
                                <ChevronDown className="h-4 w-4" />
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* Web & Socials */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(consultantDetails?.website) && (
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-gray-600" />
                          <span className="text-sm text-gray-600">{t('admin.consultants.modal.website')}</span>
                          <a
                            href={consultantDetails.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-brand-primary hover:underline flex items-center gap-1"
                          >
                            {consultantDetails.website} <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Social links - check facebook_url and linkedin_url from backend */}
                    {(consultantDetails?.facebook_url || consultantDetails?.linkedin_url) && (
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="space-y-2">
                          {consultantDetails?.facebook_url && (
                            <div className="flex items-center gap-2">
                              <ExternalLink className="h-4 w-4 text-blue-600" />
                              <a href={consultantDetails.facebook_url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-blue-600 hover:underline">
                                Facebook
                              </a>
                            </div>
                          )}
                          {consultantDetails?.linkedin_url && (
                            <div className="flex items-center gap-2">
                              <ExternalLink className="h-4 w-4 text-sky-700" />
                              <a href={consultantDetails.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-sky-700 hover:underline">
                                LinkedIn
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quick stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <BookOpen className="h-5 w-5 text-blue-600" />
                        <span className="text-sm font-semibold text-gray-700">{t('admin.consultants.modal.stats.trainings')}</span>
                      </div>
                      <p className="text-gray-900">{consultantDetails?.totalTrainings ?? selectedConsultant?.totalTrainings ?? 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <CheckCircle className={`h-5 w-5 ${consultantDetails?.isActive ?? selectedConsultant?.isActive ? 'text-green-600' : 'text-red-600'}`} />
                        <span className="text-sm font-semibold text-gray-700">{t('admin.consultants.modal.stats.status')}</span>
                      </div>
                      <p className={`font-semibold ${consultantDetails?.isActive ?? selectedConsultant?.isActive ? 'text-green-600' : 'text-red-600'}`}>
                        {consultantDetails?.isActive ?? selectedConsultant?.isActive ? t('admin.consultants.status.active') : t('admin.consultants.status.inactive')}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <CheckCircle className={`h-5 w-5 ${consultantDetails?.isVerified ?? selectedConsultant?.isVerified ? 'text-blue-600' : 'text-yellow-600'}`} />
                        <span className="text-sm font-semibold text-gray-700">{t('admin.consultants.modal.stats.verification')}</span>
                      </div>
                      <p className={`font-semibold ${consultantDetails?.isVerified ?? selectedConsultant?.isVerified ? 'text-blue-600' : 'text-yellow-600'}`}>
                        {consultantDetails?.isVerified ?? selectedConsultant?.isVerified ? t('admin.consultants.status.verified') : t('admin.consultants.status.notVerified')}
                      </p>
                    </div>
                  </div>

                  {/* Additional Info */}
                  {consultantDetails?.company_name && (
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('admin.consultants.modal.companyName')}</h3>
                      <p className="text-sm text-gray-700">{consultantDetails.company_name}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantsPage;



